const user_request_error_message = "Unable to complete user request.";

export function ensure_trailing_slash(url) {
    if (!url) {
        return "";
    }

    return url.endsWith("/") ? url : `${url}/`;
}

export function extract_user_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (payload?.id) {
        return [payload];
    }

    return [];
}

function get_user_role(user) {
    return user?.user_role?.role || null;
}

function get_nested_user_role(user) {
    return get_user_role(user) || user?.role || null;
}

function get_user_profile(user) {
    return user?.profile || null;
}

function get_user_profile_data(user) {
    return get_user_profile(user) || user?.user_profile || {};
}

function first_present_value(values, fallback = "") {
    const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== "");
    return value ?? fallback;
}

function get_normalized_is_active(user) {
    return user?.is_active !== undefined ? Boolean(user.is_active) : true;
}

function get_empty_value(value) {
    return value ?? "";
}

function get_role_label(role, fallback_role = "") {
    return typeof role === "string" ? role : role?.name || fallback_role;
}

function get_role_id(role, fallback_role_id = null) {
    return typeof role === "object" ? role?.id ?? fallback_role_id : fallback_role_id;
}

export function get_user_names(user) {
    const source_user = user || {};

    return ({
        first_name: get_empty_value(source_user.first_name),
        last_name: get_empty_value(source_user.last_name),
        username: get_empty_value(source_user.username),
        email: get_empty_value(source_user.email)
    });
}

export function get_user_role_fields(user) {
    const role = get_nested_user_role(user);

    return ({
        role: get_role_label(role, user?.user_role?.role?.name || ""),
        role_id: get_role_id(role, user?.user_role?.role?.id ?? null)
    });
}

export function get_user_activity_fields(user) {
    return ({
        is_active: user?.is_active !== false,
        last_login: user?.last_login || user?.lastLogin || ""
    });
}

export function get_user_phone_number(user) {
    const profile = get_user_profile_data(user);

    return profile?.phone_number ?? user?.phone_number ?? "";
}

export function normalize_user(user, fallback_id = "") {
    const role = get_user_role(user);
    const profile = get_user_profile(user);

    return ({
        id: first_present_value([user?.id], fallback_id),
        username: first_present_value([user?.username]),
        email: first_present_value([user?.email]),
        first_name: first_present_value([user?.first_name]),
        last_name: first_present_value([user?.last_name]),
        phone_number: first_present_value([profile?.phone_number]),
        role: first_present_value([role?.name]),
        role_id: role?.id ?? null,
        is_active: get_normalized_is_active(user),
        profile,
        user_role: user?.user_role || null,
        date_joined: user?.date_joined || "",
        raw: user
    });
}

export function extract_role_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    return [];
}

export function normalize_role_option(role) {
    if (!has_required_role_fields(role)) {
        return null;
    }

    return ({
        id: role.id,
        name: role.name,
        description: role.description || "",
        created_at: role.created_at || "",
        updated_at: role.updated_at || ""
    });
}

function has_required_role_fields(role) {
    return Boolean(role?.id) && Boolean(role?.name);
}

export function normalize_role_id(role_id) {
    if ([undefined, null, ""].includes(role_id)) {
        return null;
    }

    if (typeof role_id === "number") {
        return role_id;
    }

    const numeric = Number(role_id);
    return Number.isNaN(numeric) ? role_id : numeric;
}

function normalize_api_error_payload(error_payload) {
    return first_present_value([
        error_payload?.errors,
        error_payload?.detail,
        error_payload?.error,
        error_payload?.message
    ], error_payload);
}

function format_api_error_message(messages) {
    return Array.isArray(messages) ? messages.join(", ") : messages;
}

function format_api_error_object(error_payload) {
    return Object.entries(error_payload)
        .map(([field, messages]) => `${field}: ${format_api_error_message(messages)}`)
        .join(" | ");
}

export function format_api_errors(error_payload) {
    const normalized_payload = normalize_api_error_payload(error_payload);
    const error_formatters = [
        [(value) => !value, () => user_request_error_message],
        [(value) => typeof value === "string", (value) => value],
        [Array.isArray, (value) => value.join(", ")],
        [(value) => typeof value === "object", format_api_error_object]
    ];
    const formatter = error_formatters.find(([matches]) => matches(normalized_payload));

    return formatter ? formatter[1](normalized_payload) : user_request_error_message;
}
